import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import LoadingOverlay from '../components/overlays/LoadingOverlay';
import AlertModal from '../components/modals/AlertModal';
import { useAuth } from '../contexts/AuthContext';
import landingLogo from './onboarding/assets/acuity-logo.png';
import './InquiriesManager.css';

const FAMILY_SOURCE = 'landing-families';

const SOURCE_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'landing', label: 'Schools' },
  { id: FAMILY_SOURCE, label: 'Families' }
];

function formatReceived(iso) {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  const time = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  if (date.toDateString() === new Date().toDateString()) return `Today, ${time}`;
  return `${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}, ${time}`;
}

/** Render a stored answer the way a person would write it. */
function readableAnswer(value) {
  if (Array.isArray(value)) {
    if (value.length && typeof value[0] === 'object') {
      return value
        .map((item, i) => {
          const name = [item.first_name, item.last_name].filter(Boolean).join(' ');
          const grade = item.grade ? (item.grade === 'K' ? 'Kindergarten' : `Grade ${item.grade}`) : '';
          return `${i + 1}. ${name}${grade ? ` — ${grade}` : ''}`;
        })
        .join('\n');
    }
    return value.join(', ');
  }
  return String(value == null ? '' : value);
}

function isFamily(row) {
  return row?.source === FAMILY_SOURCE;
}

/** Pair each stored question with its answer, so old rows stay readable after the form changes. */
function answeredPairs(row) {
  const questions = Array.isArray(row?.questions) ? row.questions : [];
  const answers = row?.answers || {};
  return questions
    .map((q) => ({ id: q.id, label: q.label, value: readableAnswer(answers[q.id]) }))
    .filter((pair) => pair.value);
}

const InquiriesManager = () => {
  const { fetchWithAuth } = useAuth();
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const [alertModal, setAlertModal] = useState(null);

  const loadRows = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await fetchWithAuth('/api/inquiry');
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Failed to load inquiries');
      setRows(data.data || []);
    } catch (err) {
      setAlertModal({
        variant: 'error',
        subject: 'Load failed',
        body: 'Could not load inquiries.',
        details: err.message,
        actions: [{ label: 'OK', onClick: () => setAlertModal(null), variant: 'primary' }]
      });
    } finally {
      setIsLoading(false);
    }
  }, [fetchWithAuth]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const visibleRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return rows.filter((row) => {
      if (sourceFilter !== 'all' && row.source !== sourceFilter) return false;
      if (!term) return true;
      return [row.contact_name, row.school_name, row.email, row.phone, row.reference]
        .join(' ')
        .toLowerCase()
        .includes(term);
    });
  }, [rows, search, sourceFilter]);

  useEffect(() => {
    if (!selected) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setSelected(null);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [selected]);

  const exportCsv = () => {
    if (!visibleRows.length) return;

    const ids = [];
    const labels = {};
    visibleRows.forEach((row) => {
      (row.questions || []).forEach((q) => {
        if (!ids.includes(q.id)) ids.push(q.id);
        labels[q.id] = q.label;
      });
    });

    const head = ['Reference', 'Received', 'Type', 'School / program', 'Contact', 'Email', 'Phone']
      .concat(ids.map((id) => labels[id] || id));
    const cell = (v) => `"${String(v == null ? '' : v).replace(/"/g, '""')}"`;

    const lines = [head.map(cell).join(',')];
    visibleRows.forEach((row) => {
      const base = [
        row.reference,
        new Date(row.created_at).toLocaleString('en-US'),
        isFamily(row) ? 'Family' : 'School',
        row.school_name,
        row.contact_name,
        row.email,
        row.phone
      ];
      const answers = row.answers || {};
      lines.push(
        base
          .concat(ids.map((id) => readableAnswer(answers[id]).replace(/\n/g, '; ')))
          .map(cell)
          .join(',')
      );
    });

    const blob = new Blob([`\uFEFF${lines.join('\r\n')}`], { type: 'text/csv;charset=utf-8' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `acuity-inquiries-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  return (
    <div className="inq-page">
      <LoadingOverlay isVisible={isLoading} message="Loading inquiries..." />

      <header className="inq-bar">
        <div className="inq-bar-inner">
          <Link className="inq-bar-logo" to="/onboarding" aria-label="Acuity Learn — request access for your school or district">
            <img src={landingLogo} alt="Acuity Learn" />
          </Link>
          <div className="inq-bar-actions">
            <Link className="inq-btn" to="/onboarding">School form</Link>
            <Link className="inq-btn" to="/families">Family form</Link>
          </div>
        </div>
      </header>

      <div className="inq-main">
        <div className="inq-head">
          <div>
            <p className="inq-eyebrow">Onboarding</p>
            <h1>
              {visibleRows.length} {visibleRows.length === 1 ? 'inquiry' : 'inquiries'}
            </h1>
          </div>
          <div className="inq-filters">
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, school, email…"
              aria-label="Search inquiries"
            />
            <div className="inq-seg" role="tablist" aria-label="Filter by type">
              {SOURCE_FILTERS.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  role="tab"
                  aria-selected={sourceFilter === option.id}
                  className={`inq-seg-btn${sourceFilter === option.id ? ' is-on' : ''}`}
                  onClick={() => setSourceFilter(option.id)}
                >
                  {option.label}
                </button>
              ))}
            </div>
            <button type="button" className="inq-btn" onClick={exportCsv} disabled={!visibleRows.length}>
              Export CSV
            </button>
          </div>
        </div>

      {!isLoading && visibleRows.length === 0 ? (
        <div className="inq-empty">
          {rows.length === 0
            ? "No inquiries yet. They'll appear here as they come in."
            : 'No inquiries match your search.'}
        </div>
      ) : (
        <div className="inq-table-wrap">
          <table className="inq-table">
            <thead>
              <tr>
                <th>Received</th>
                <th>Name</th>
                <th>School / program</th>
                <th>Type</th>
                <th>Contact</th>
                <th className="inq-num">Ref</th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((row) => (
                <tr
                  key={row.id}
                  tabIndex={0}
                  onClick={() => setSelected(row)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setSelected(row);
                    }
                  }}
                >
                  <td className="inq-when">{formatReceived(row.created_at)}</td>
                  <td>
                    <span className="inq-name">{row.contact_name}</span>
                  </td>
                  <td>{row.school_name}</td>
                  <td>
                    <span className={`inq-tag inq-tag--${isFamily(row) ? 'family' : 'school'}`}>
                      {isFamily(row) ? 'Family' : 'School'}
                    </span>
                  </td>
                  <td>
                    {row.email || '—'}
                    {row.phone ? <span className="inq-sub">{row.phone}</span> : null}
                  </td>
                  <td className="inq-ref">{row.reference}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      </div>

      {selected && (
        <div className="inq-drawer">
          <div className="inq-drawer-scrim" onClick={() => setSelected(null)} />
          <aside
            className="inq-drawer-panel"
            role="dialog"
            aria-modal="true"
            aria-label="Inquiry detail"
          >
            <div className="inq-drawer-head">
              <div>
                <p className="inq-eyebrow">
                  {isFamily(selected) ? 'Family pre-registration' : 'School / district inquiry'}
                </p>
                <h2 className="inq-drawer-title">{selected.school_name}</h2>
                <p className="inq-drawer-meta">
                  {selected.contact_name}
                  {selected.email ? (
                    <>
                      {' · '}
                      <a href={`mailto:${selected.email}`}>{selected.email}</a>
                    </>
                  ) : null}
                  {selected.phone ? ` · ${selected.phone}` : ''}
                </p>
              </div>
              <button
                type="button"
                className="inq-drawer-close"
                onClick={() => setSelected(null)}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="inq-drawer-body">
              <dl className="inq-qa">
                {answeredPairs(selected).map((pair) => (
                  <React.Fragment key={pair.id}>
                    <dt>{pair.label}</dt>
                    <dd>{pair.value}</dd>
                  </React.Fragment>
                ))}
              </dl>
              <p className="inq-drawer-ref">
                Reference {selected.reference} · received{' '}
                {new Date(selected.created_at).toLocaleString('en-US')}
              </p>
            </div>
          </aside>
        </div>
      )}

      {alertModal && (
        <AlertModal
          isOpen
          onClose={() => setAlertModal(null)}
          variant={alertModal.variant}
          subject={alertModal.subject}
          body={alertModal.body}
          details={alertModal.details}
          actions={alertModal.actions}
        />
      )}
    </div>
  );
};

export default InquiriesManager;
